import React, { useCallback, useEffect, useRef, useState } from 'react';
import './CriteriaSetting.css';
import {
  Criteria,
  GetCriteriaDistributionResponseType,
} from '../../../../types/screener.type';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-line.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down.svg';
import RangeSlider from '../../../Common/RangeSlider/RangeSlider';
import RangeSilder from '../../../Common/RangeSlider/RangeSlider';
import {
  AC_GROUPS,
  AC_TYPES,
} from '../../../../constants/screener/AC.types.constant';
import useDebounce from '../../../../hooks/useDebounce';
import {
  logValue2TrueValue,
  truevalue2logvalue,
} from '../../../../utils/utils';
import { IDictionary } from '../../../../types/common.type';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Box, Button } from '@mui/material';
import { getCriteriaDistribution } from '../../../../api/screener.api';
import { isArray } from 'lodash';
import { config } from 'dotenv';

export interface CriteriaSettingProps {
  region: string;
  sector: string;
  tempCriteria: Criteria[];
  criteria: Criteria;
  setCriteria: any;
  token: string;
}

type PositionTypes = 'UPPER' | 'LOWER';

const scores = Array.from({ length: 11 }, (_, i) => 0 + i);

const valueMapping: IDictionary<string> = {
  yoy: 'Year over Year',
  '1m': '1 month',
  '2m': '2 months',
  '3m': '3 months',
  '6m': '6 months',
  '9m': '9 months',
  '12m': '12 months',
  '1y': '1 Year',
  '3y': '3 Years',
  '5y': '5 Years',
  '10y': '10 Years',
  '1q': '1 Quarter',
  '1d': '1 Day',
  '5d': '5 Days',
  wtd: 'Week to Date',
  '4wk': '4 Weeks',
  mtd: 'Month to Date',
  '13wk': '13 Weeks',
  qtd: 'Quarter to Date',
  '26wk': '26 Weeks',
  '52wk': '52 Weeks',
  ytd: 'Year to Date',
  '50D': '50 Days',
  '150D': '150 Days',
  '200D': '200 Days',
  '10d': '10 Days',
};

const CriteriaSetting = ({
  region,
  sector,
  tempCriteria,
  criteria,
  setCriteria,
  token,
}: CriteriaSettingProps) => {
  const rangeRatio = criteria.type === AC_TYPES.score ? 1 : 0.01;
  // const rangeRatio = 1;

  const [minRange, setMinRange] = useState(0);
  const [maxRange, setMaxRange] = useState(0);

  const [trueMin, setTrueMin] = useState(0);
  const [trueMax, setTrueMax] = useState(0);

  const [tempLower, setTempLower] = useState('0');
  const [tempUpper, setTempUpper] = useState('0');

  const [tscaleLower, setTscaleLower] = useState(0);
  const [tscaleUpper, setTscaleUpper] = useState(0);

  const [scaleStep, setScaleStep] = useState(0);
  const [scaleMid, setScaleMid] = useState(0);

  const [selectedMin, setSelectedMin] = useState<number | undefined>(undefined);
  const [selectedMax, setSelectedMax] = useState<number | undefined>(undefined);

  const [timeValue, setTimeValue] = useState(
    criteria.timeValue ? criteria.timeValue : criteria.timeOptions?.[0] || '',
  );

  const isMacroSensitivity = criteria.group === AC_GROUPS.macroSensitivity;

  const [decimalPlaces, setDecimalPlaces] = useState(
    criteria.type === AC_TYPES.score ? 0 : 2,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const input2Ref = useRef<HTMLInputElement>(null);

  const tempCriteriaRef = useRef(tempCriteria);
  tempCriteriaRef.current = tempCriteria;

  useEffect(() => {
    if (criteria.type === AC_TYPES.score) {
      setSelectedMin(criteria.selectedMin ?? 0);
      setSelectedMax(criteria.selectedMax ?? 10);
      return;
    }
    let cancelled = false;
    getMinMax(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [timeValue]);

  useEffect(() => {
    if (selectedMin !== undefined && selectedMax !== undefined) {
      updateSelectedCriteria(criteria.id, selectedMin, selectedMax, timeValue);
    }
  }, [selectedMin, selectedMax, timeValue]);

  const filterFloat = (value: string) => {
    if (/^(-|\+)?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value))
      return Number(value);
    return NaN;
  };

  const setValues = useCallback(
    (
      trueMin: number,
      trueMax: number,
      mid: number,
      timeValue: string,
      decimal = 2,
    ) => {
      if (isMacroSensitivity || criteria.type === AC_TYPES.score) {
        setMinRange(trueMin);
        setMaxRange(trueMax);
      } else {
        setMinRange(trueMin);
        setMaxRange(trueMax);
      }
      if (
        criteria.selectedMin !== undefined &&
        criteria.timeValue === timeValue
      ) {
        setTscaleLower(truevalue2logvalue(criteria.selectedMin, mid));
      } else {
        setTscaleLower(trueMin);
      }
      if (
        criteria.selectedMax !== undefined &&
        criteria.timeValue === timeValue
      ) {
        setTscaleUpper(truevalue2logvalue(criteria.selectedMax, mid));
      } else {
        setTscaleUpper(trueMax);
      }
    },
    [],
  );

  const getMinMax = async (isCancelled: () => boolean) => {
    // Set pendingMinMax flag when API starts
    const latestData = tempCriteriaRef.current.map((c: Criteria) =>
      c.id === criteria.id && c.withRange
        ? { ...c, pendingMinMax: true, timeValue }
        : c,
    );
    setCriteria(latestData);

    try {
      const response = await getCriteriaDistribution(
        criteria.id.split('_')[0],
        timeValue,
        region,
        sector,
        token,
      );

      if (isCancelled()) return;

      const { scalemax, scalemin, scalestep, truemax, truemid, truemin } =
        response;
      setValues(scalemin, scalemax, truemid, timeValue);
      setTrueMin(truemin);
      setTrueMax(truemax);
      setScaleStep(scalestep);
      setScaleMid(truemid);

      let newSelectedMin: number;
      if (
        criteria.selectedMin !== undefined &&
        criteria.timeValue === timeValue
      ) {
        newSelectedMin = criteria.selectedMin;
        setTempLower(criteria.selectedMin.toString());
      } else {
        newSelectedMin = truemin;
        setTempLower(truemin.toFixed(decimalPlaces));
      }
      setSelectedMin(newSelectedMin);

      let newSelectedMax: number;
      if (
        criteria.selectedMax !== undefined &&
        criteria.timeValue === timeValue
      ) {
        newSelectedMax = criteria.selectedMax;
        setTempUpper(criteria.selectedMax.toString());
      } else {
        newSelectedMax = truemax;
        setTempUpper(truemax.toFixed(decimalPlaces));
      }
      setSelectedMax(newSelectedMax);

      const latestSuccessData = tempCriteriaRef.current.map((c: Criteria) =>
        c.id === criteria.id
          ? {
              ...c,
              min: scalemin,
              max: scalemax,
              selectedMin: newSelectedMin,
              selectedMax: newSelectedMax,
              timeValue,
              pendingMinMax: false,
            }
          : c,
      );
      setCriteria(latestSuccessData);
    } catch (error) {
      console.log('Get Criteria Distribution Error:', error);
      if (isCancelled()) return;
      // Clear pendingMinMax flag even on error
      const latestErrorData = tempCriteriaRef.current.map((c: Criteria) =>
        c.id === criteria.id ? { ...c, pendingMinMax: false } : c,
      );
      setCriteria(latestErrorData);
    }
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (scaleMid !== undefined && isArray(newValue)) {
      const newLower = logValue2TrueValue(newValue[0], scaleMid).toFixed(
        decimalPlaces,
      );
      const newUpper = logValue2TrueValue(newValue[1], scaleMid).toFixed(
        decimalPlaces,
      );
      setSelectedMin(parseFloat(newLower));
      setSelectedMax(parseFloat(newUpper));
      setTempLower(newLower);
      setTempUpper(newUpper);
      setTscaleLower(newValue[0]);
      setTscaleUpper(newValue[1]);
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: PositionTypes,
  ) => {
    const inputValue = event.target.value;
    if (isNaN(filterFloat(inputValue))) {
      if (inputValue == '-' || inputValue == '') {
        if (type === 'LOWER') {
          setTempLower(inputValue);
        } else {
          setTempUpper(inputValue);
        }
        return;
      }
      if (inputValue.substring(inputValue.length - 1) == '.') {
        if (type === 'LOWER') {
          setTempLower(inputValue);
        } else {
          setTempUpper(inputValue);
        }
        return;
      }

      const newInput = inputValue.substring(0, inputValue.length - 1);

      if (isNaN(filterFloat(newInput))) {
        if (type === 'LOWER') {
          setTempLower('');
        } else {
          setTempUpper('');
        }
      } else {
        if (type === 'LOWER') {
          setTempLower(newInput);
        } else {
          setTempUpper(newInput);
        }
      }
      return;
    } else {
      if (type === 'LOWER') {
        setTempLower(inputValue);
      } else {
        setTempUpper(inputValue);
      }
    }
  };

  const handleInputBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    type: PositionTypes,
  ) => {
    const inputValue = event.target.value;
    let newValue = !isNaN(parseFloat(inputValue))
      ? inputValue
      : trueMin.toFixed(decimalPlaces);

    if (type === 'LOWER') {
      if (parseFloat(inputValue) < trueMin) {
        newValue = trueMin.toFixed(decimalPlaces);
        setTempLower(newValue);
      } else if (parseFloat(inputValue) > parseFloat(tempUpper)) {
        setTempLower(tempUpper);
        newValue = tempUpper;
      }
      setSelectedMin(parseFloat(newValue));
      setTscaleLower(truevalue2logvalue(parseFloat(newValue), scaleMid));
    } else {
      if (parseFloat(inputValue) > trueMax) {
        newValue = trueMax.toFixed(decimalPlaces);
        setTempUpper(newValue);
      } else if (parseFloat(inputValue) < parseFloat(tempLower)) {
        setTempUpper(tempLower);
        newValue = tempLower;
      }
      setSelectedMax(parseFloat(newValue));
      setTscaleUpper(truevalue2logvalue(parseFloat(newValue), scaleMid));
    }
  };

  const handleInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    type: PositionTypes,
  ) => {
    if (event.key === 'Enter') {
      if (type === 'LOWER') {
        if (parseFloat(tempLower) < trueMin) {
          setTempLower(trueMin.toFixed(decimalPlaces));
          setSelectedMin(trueMin);
          setTscaleLower(truevalue2logvalue(trueMin, scaleMid));
        } else if (parseFloat(tempLower) > parseFloat(tempUpper)) {
          setTempLower(tempUpper);
          setSelectedMin(parseFloat(tempUpper));
          setTscaleLower(truevalue2logvalue(parseFloat(tempUpper), scaleMid));
        } else {
          setSelectedMin(parseFloat(tempLower));
          setTscaleLower(truevalue2logvalue(parseFloat(tempLower), scaleMid));
        }
      } else {
        if (parseFloat(tempUpper) > trueMax) {
          setTempUpper(trueMax.toFixed(decimalPlaces));
          setSelectedMax(trueMax);
          setTscaleUpper(truevalue2logvalue(trueMax, scaleMid));
        } else if (parseFloat(tempUpper) < parseFloat(tempLower)) {
          setTempUpper(tempLower);
          setSelectedMax(parseFloat(tempLower));
          setTscaleUpper(truevalue2logvalue(parseFloat(tempLower), scaleMid));
        } else {
          setSelectedMax(parseFloat(tempUpper));
          setTscaleUpper(truevalue2logvalue(parseFloat(tempUpper), scaleMid));
        }
      }
    }
  };

  const updateSelectedCriteria = (
    id: string,
    selectedMin: number,
    selectedMax: number,
    timeValue: string,
    scores?: (number | string)[],
  ) => {
    const latestData = tempCriteria.map((c: Criteria) =>
      c.id === id
        ? {
            ...c,
            ...(c.timeValue !== timeValue && c.withRange
              ? { timeValue, pendingMinMax: true }
              : {
                  max: maxRange,
                  min: minRange,
                  selectedMin,
                  selectedMax,
                  timeValue,
                  pendingMinMax: false,
                }),
            ...(typeof scores !== 'undefined' ? { scores } : {}),
            ...(c.isInitial ? { isInitial: false } : {}),
          }
        : c,
    );
    setCriteria(latestData);
  };

  const handleScoreClick = (score: number) => {
    const currentMin = selectedMin ?? 0;
    const currentMax = selectedMax ?? 10;
    let newMin = currentMin;
    let newMax = currentMax;

    if (score <= currentMin) {
      newMin = score;
    } else if (score >= currentMax) {
      newMax = score;
    } else {
      if (score - currentMin < currentMax - score) {
        newMin = score;
      } else {
        newMax = score;
      }
    }

    setTempLower(newMin.toString());
    setTempUpper(newMax.toString());
    setSelectedMin(newMin);
    setSelectedMax(newMax);
  };

  const handleRemoveCriteria = () => {
    const newCriteria = [...tempCriteria];
    const index = newCriteria.findIndex(
      (item: Criteria) => item.id === criteria.id,
    );
    if (index !== -1) {
      newCriteria[index] = { ...newCriteria[index], selected: false };
    }
    setCriteria(newCriteria);
  };

  return (
    <div className="screener-criteria-setting">
      <div className="screener-criteria-setting-header">
        <div className="screener-criteria-setting-header-title">
          {criteria.label}
        </div>
        <div className="screener-criteria-setting-header-close">
          <CloseIcon onClick={handleRemoveCriteria} />
        </div>
      </div>

      {criteria.type === AC_TYPES.score && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: '2px',
          }}
        >
          {scores.map((score) => {
            const isInRange =
              score >= (selectedMin ?? 0) && score <= (selectedMax ?? 10);
            return (
              <Button
                key={score}
                color="primary"
                onClick={() => handleScoreClick(score)}
                sx={{
                  flex: 1,
                  minWidth: '0px',
                  height: '30px',
                  padding: '4px 8px',
                  borderRadius: '1px',
                  backgroundColor: isInRange
                    ? '#7F7F7F'
                    : 'rgba(116, 116, 128, 0.08)',
                  color: isInRange ? '#FFFFFF' : '#1E1F2F',
                  '&:hover': {
                    backgroundColor: isInRange
                      ? '#7F7F7F'
                      : 'rgba(116, 116, 128, 0.12)',
                  },
                  fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: '100%',
                }}
              >
                {score}
              </Button>
            );
          })}
        </Box>
      )}

      {criteria.withScoreRange && (
        <div className="screener-criteria-setting-slider-container">
          <RangeSilder
            min={0}
            max={10}
            step={1}
            value={[selectedMin ?? 0, selectedMax ?? 10]}
            handleChange={(_: Event, value: number | number[]) => {
              if (Array.isArray(value)) {
                setSelectedMin(value[0]);
                setSelectedMax(value[1]);
                setTempLower(String(value[0]));
                setTempUpper(String(value[1]));
              }
            }}
          />
        </div>
      )}

      {criteria.withRange && (
        <div className="screener-criteria-setting-slider-container">
          {/* {isMacroSensitivity ?
                        <RangeSilder
                            min={minRange}
                            max={maxRange}
                            step={rangeRatio}
                            value={[tscaleLower, tscaleUpper]}
                            handleChange={(event: Event, value: number | number[]) => {
                                handleSliderChange(event, value);
                            }}
                        ></RangeSilder> : */}
          <RangeSilder
            min={minRange}
            max={maxRange}
            step={scaleStep}
            value={[tscaleLower, tscaleUpper]}
            handleChange={(event: Event, value: number | number[]) => {
              handleSliderChange(event, value);
            }}
          ></RangeSilder>
          {/* } */}
        </div>
      )}

      {criteria.type === AC_TYPES.time && (
        <Select
          className="screener-criteria-setting-time-select"
          sx={{
            width: '120px',
            height: '40px',
            borderRadius: '4px',
            strokeColor: '#bfbfbf',
            strokeWidth: '1px',
            border: `1px solid var(--border-primary)`,
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#1E1F2F',
            '& .MuiSelect-select': {
              height: '100%',
              textAlign: 'left',
              boxSizing: 'border-box',
              padding: '8px 8px 8px 16px',
              alignContent: 'center',
              color: '#1E1F2F',
            },
            '& .MuiSelect-icon': {
              width: '16px',
              height: '16px',
              right: '4px',
            },
            '&:hover': {
              backgroundColor: 'var(--color-brand-hover2)',
            },
          }}
          variant="standard"
          disableUnderline={true}
          value={timeValue}
          onChange={(event) => setTimeValue(event.target.value)}
          IconComponent={ArrowDownIcon}
        >
          {criteria.timeOptions?.map((timeOption) => (
            <MenuItem
              sx={{
                padding: '4px 8px',
                fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                color: '#1E1F2F',
                '&:hover': {
                  backgroundColor: 'var(--color-brand-hover2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--color-brand-hover2)',
                  '&:hover': {
                    backgroundColor: 'var(--color-brand-hover2)',
                  },
                },
              }}
              key={timeOption}
              value={timeOption}
            >
              {valueMapping[timeOption]}
            </MenuItem>
          ))}
        </Select>
      )}

      {criteria.withRange && (
        <div className="screener-criteria-setting-input-container">
          <input
            ref={inputRef}
            className="screener-criteria-setting-input"
            id="number"
            type="text"
            inputMode="decimal"
            pattern="[+-]?([0-9]*[.])?[0-9]{0,4}"
            value={tempLower}
            onChange={(event) => handleInputChange(event, 'LOWER')}
            onBlur={(event) => handleInputBlur(event, 'LOWER')}
            onKeyDown={(event) => handleInputKeyDown(event, 'LOWER')}
          />

          <div className="screener-criteria-setting-input-label">TO</div>

          <input
            ref={input2Ref}
            className="screener-criteria-setting-input"
            id="number"
            type="text"
            inputMode="decimal"
            value={tempUpper}
            pattern="[+-]?([0-9]*[.])?[0-9]{0,4}"
            onChange={(event) => handleInputChange(event, 'UPPER')}
            onBlur={(event) => handleInputBlur(event, 'UPPER')}
            onKeyDown={(event) => handleInputKeyDown(event, 'UPPER')}
          />
        </div>
      )}
    </div>
  );
};

export default CriteriaSetting;
